import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TableBody as Body, SelectChangeEvent } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { Badge, CustomSelect, TableNoData } from '../../shared/uiComponents';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import { HeaderProperties, TableDataProperties } from '../../shared/uiComponents/Table/tableProperties';
import CustomDrawer from '../../shared/uiComponents/Drawer';
import UserAction from '../../components/UserAction';

import { UserProperties } from '../../redux/API/userAPIProperties';
import { DispatchProperties, useSelector } from '../../redux/store';
import { getCaseCoordinators } from '../../redux/State/clientSlice/userClientSlice';
import { getBTs } from '../../redux/State/userSlice';
import { getBranchBadgeType } from '../../shared/Helpers/constants';

export interface AdminDataProperties extends TableDataProperties {
	query: UserProperties[] | null;
}

interface RowRendererProperties {
	data: UserProperties[];
}

export const Headers: HeaderProperties[] = [
	{ id: '7', name: 'Full Name', orderBy: 'fullName' },
	{
		id: '1',
		name: 'Number of Clients',
		orderBy: 'numberOfClients',
	},
	{ id: '2', name: 'Email Status', orderBy: 'emailConfirmed', width: '150px' },
	{ id: '3', name: 'Status', orderBy: 'isDeactivated', width: '100px' },
	{ id: '4', name: 'Role' },
	{ id: '8', name: 'Branch' },
	{ id: '5', name: 'Case Manager' },
	{ id: '6', name: 'Actions', width: '120px' },
];

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();

	const fetchUsers = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const orderBy = params.get('orderBy') || '';
		const direction = params.get('direction') || '';
		const searchString = params.get('search') || '';

		dispatch(getBTs({ page, pageSize, orderBy, direction, searchString }));
	};

	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => (
				<CustomTableRow key={row.id ?? index}>
					<RowTableCell>{row.fullName}</RowTableCell>
					<RowTableCell>{row.numberOfClients}</RowTableCell>
					<RowTableCell>
						<Badge
							label={row.emailConfirmed ? 'Confirmed' : 'Pending'}
							sizeType="sm"
							type={row.emailConfirmed ? 'solidSuccess' : 'solidPrimary'}
						/>
					</RowTableCell>
					<RowTableCell>
						<Badge
							label={row.isDeactivated ? 'Inactive' : 'Active'}
							sizeType="sm"
							type={row.isDeactivated ? 'gray' : 'success'}
						/>
					</RowTableCell>
					<RowTableCell>{row.role?.name || '-'}</RowTableCell>
					<RowTableCell>
						<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
							{row.branches && row.branches.length > 0 ? (
								row.branches.map((branch) => (
									<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
								))
							) : (
								<span style={{ color: '#999', fontSize: '12px' }}>None</span>
							)}
						</div>
					</RowTableCell>
					<RowTableCell>
						<div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
							{row?.caseCoordinators?.length
								? row.caseCoordinators.map((x, itemIndex) => <span key={x.id || itemIndex}>{x.fullName}</span>)
								: '-'}
						</div>
					</RowTableCell>
					<RowTableCell>
						<UserAction item={row} fetchUsers={fetchUsers} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};

export const BtFilters = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params, setParams] = useSearchParams();

	const caseCoordinators = useSelector((state) => state.userClient.caseCoordinators);

	const data = caseCoordinators ? caseCoordinators.map((x) => ({ id: x.id, name: x.fullName })) : [];
	const open = params.get('filter') === 'open';
	const caseCoordinatorId = params.get('caseCoordinatorId') || '';

	const closeHandler = () => {
		params.set('filter', 'close');
		setParams(params, { replace: true });
	};

	const onCaseCoordinatorChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		params.set('caseCoordinatorId', value);
		setParams(params, { replace: true });
	};

	useEffect(() => {
		dispatch(getCaseCoordinators());
	}, [dispatch]);

	return (
		<CustomDrawer
			open={open}
			onClose={closeHandler}
			label="Filters"
			isSecondary
			Content={() => (
				<CustomSelect
					label="Select case manager: "
					setValue={onCaseCoordinatorChange}
					value={caseCoordinatorId}
					data={[{ id: '', name: 'Clear' }, ...data]}
				/>
			)}
		/>
	);
};
